/**
 * TTEvsMTTR — the two-bar exploitation race for a single CVE.
 *
 * Red bar  = predicted-TTE   ("the exploit lands here")
 * Blue bar = our-MTTR        ("we patch here")
 * When red < blue the gap between them is shaded as the EXPOSURE window — the
 * days we'd be exploited before we can patch. Adapted from the RaceBar on the
 * Exploitation Race page so the CVE-detail view shows the identical visual.
 *
 * Colour thresholds for the predicted-TTE label stay consistent with TTEBadge:
 *   red ≤ 2d   amber ≤ 7d   blue > 7d
 */
import { clsx } from 'clsx'
import type { TTEData } from './TTEBadge'

export default function TTEvsMTTR({ tte, compact }: { tte: TTEData; compact?: boolean }) {
  const ttd = tte.predicted_tte_days ?? 0
  const mttr = tte.our_mttr_days ?? 0
  const axisMax = Math.max(mttr, ttd, 1) * 1.08
  const pct = (d: number) => `${Math.max(0, Math.min(100, (d / axisMax) * 100))}%`
  const losing = tte.losing ?? mttr > ttd
  const expStart = Math.min(ttd, mttr)
  const expWidth = Math.abs(mttr - ttd)
  const barH = compact ? 'h-3' : 'h-4'

  return (
    <div className="w-full select-none">
      {/* exploit bar (red) */}
      <div className={clsx('relative mb-1', barH)}>
        <div className="absolute inset-0 rounded bg-surface-raised" />
        <div className="absolute inset-y-0 left-0 rounded bg-rose/60" style={{ width: pct(ttd) }}
             title={`predicted exploit window ≈ ${ttd}d`} />
        {losing && expWidth > 0 && (
          <div className="absolute inset-y-0 rounded bg-rose/15 border-y border-dashed border-rose/40"
               style={{ left: pct(expStart), width: pct(expWidth) }}
               title={`exposure window: ${expWidth.toFixed(1)}d exploited before we patch`} />
        )}
      </div>
      {/* patch bar (blue) */}
      <div className={clsx('relative', barH)}>
        <div className="absolute inset-0 rounded bg-surface-raised" />
        <div className="absolute inset-y-0 left-0 rounded bg-cyan/50" style={{ width: pct(mttr) }}
             title={`our realistic remediation window ≈ ${mttr}d`} />
      </div>
      <div className="flex justify-between mt-1 text-[9px] text-text-muted">
        <span><span className="text-rose-light">▮</span> exploit {ttd}d</span>
        {losing
          ? <span className="text-rose-light font-semibold">exposed {expWidth.toFixed(1)}d</span>
          : <span className="text-emerald">we win by {Math.abs(expWidth).toFixed(1)}d</span>}
        <span><span className="text-cyan">▮</span> patch {mttr}d</span>
      </div>
    </div>
  )
}
